import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

const API = `${BACKEND_URL}/api`;
const ACHIEVEMENT_ID = 'stop-the-trace';

const StopTheTrace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [resultText, setResultText] = useState('');
  const [traceStatus, setTraceStatus] = useState('TRACE IN PROGRESS — TAP <strong>ABORT</strong> INSIDE THE SAFE ZONE.');
  const [attempts, setAttempts] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const markerPosRef = useRef(0);
  const directionRef = useRef(1);
  const animIdRef = useRef(null);
  const traceBarRef = useRef(null);
  const safeZoneRef = useRef(null);
  const traceMarkerRef = useRef(null);
  
  const maxAttempts = 2;

  useEffect(() => {
    // Start typing animation
    setTimeout(() => {
      setGameStarted(true);
      startTrace();
    }, 1800);

    return () => {
      if (animIdRef.current) {
        cancelAnimationFrame(animIdRef.current);
      }
    };
  }, []);

  const startTrace = () => {
    setAttempts(0);
    setResultText('');
    setTraceStatus('TRACE IN PROGRESS — TAP <strong>ABORT</strong> INSIDE THE SAFE ZONE.');
    
    // Initialize marker position randomly
    markerPosRef.current = 10 + Math.random() * 80;
    directionRef.current = Math.random() > 0.5 ? 1 : -1;

    if (animIdRef.current) cancelAnimationFrame(animIdRef.current);
    animateMarker();
  };

  const animateMarker = () => {
    const speed = 0.8;
    markerPosRef.current += directionRef.current * speed;

    if (markerPosRef.current <= 2) {
      markerPosRef.current = 2;
      directionRef.current = 1;
    } else if (markerPosRef.current >= 98) {
      markerPosRef.current = 98;
      directionRef.current = -1;
    }

    if (traceBarRef.current && traceMarkerRef.current) {
      const barWidth = traceBarRef.current.clientWidth;
      const x = (markerPosRef.current / 100) * barWidth;
      traceMarkerRef.current.style.left = x + 'px';
    }

    animIdRef.current = requestAnimationFrame(animateMarker);
  };

  const checkHit = () => {
    const currentAttempt = attempts + 1;
    setAttempts(currentAttempt);

    if (!traceBarRef.current || !safeZoneRef.current) return;

    const barWidth = traceBarRef.current.clientWidth;
    const safeRect = safeZoneRef.current.getBoundingClientRect();
    const barRect = traceBarRef.current.getBoundingClientRect();
    
    const safeLeftPct = ((safeRect.left - barRect.left) / barWidth) * 100;
    const safeRightPct = ((safeRect.right - barRect.left) / barWidth) * 100;

    const inSafe = markerPosRef.current >= safeLeftPct && markerPosRef.current <= safeRightPct;

    if (inSafe) {
      onSuccess();
    } else {
      onFail(currentAttempt);
    }
  };

  const onSuccess = async () => {
    if (animIdRef.current) cancelAnimationFrame(animIdRef.current);
    
    setIsSuccess(true);
    setTraceStatus('TRACE ABORTED — CHANNEL SECURED.');
    setResultText('Override accepted. Declassifying file…');

    // Store achievement in sessionStorage (will be claimed on login)
    sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);

    setTimeout(() => {
      setShowOffer(true);
      setTimeout(() => {
        navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
      }, 1300);
    }, 900);
  };

  const onFail = (currentAttempt) => {
    setIsSuccess(false);
    
    if (currentAttempt < maxAttempts) {
      setResultText('Too close. Trace nearly locked. You get one more shot.');
      setTraceStatus('TRACE ESCALATING — HIT THE SAFE ZONE THIS TIME.');
    } else {
      setResultText('Trace completed. You are… very on the grid. Opening file anyway.');
      setTraceStatus('TRACE COMPLETE — OVERRIDE FORCED BY SYSTEM.');
      
      if (animIdRef.current) cancelAnimationFrame(animIdRef.current);

      // Store achievement even on fail (forgiving UX)
      sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);

      setTimeout(() => {
        setShowOffer(true);
        setTimeout(() => {
          navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
        }, 1300);
      }, 1100);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={{...styles.terminal, ...(showOffer ? styles.fadeOut : {})}} className={showOffer ? 'fade-out' : ''}>
        <div style={styles.terminalHeader}>
          <div style={styles.dots}>
            <span style={styles.dot}></span>
            <span style={styles.dot}></span>
            <span style={styles.dot}></span>
          </div>
          <div style={styles.headerLabel}>F.B.I. ARCHIVAL GATEWAY</div>
        </div>

        <div style={styles.terminalBody}>
          <div style={styles.line}>
            <span style={styles.label}>SYSTEM:</span>
            <span style={styles.value}> F.B.I. ARCHIVAL GATEWAY v4.2</span>
          </div>
          <div style={styles.line}>
            <span style={styles.label}>ROUTE:</span>
            <span style={styles.value}> TPPD-NODE-07 / LOCAL ACCESS</span>
          </div>
          <div style={styles.line}>
            <span style={styles.label}>DOCUMENT:</span>
            <span style={styles.value}> TAPPD // LOCAL BUSINESS OFFER</span>
          </div>
          <div style={styles.line}>
            <span style={styles.label}>STATUS:</span>
            <span style={{...styles.value, color: '#ef4444'}}> CLASSIFIED</span>
          </div>
          <div style={styles.line}>
            <span style={styles.label}>AUTH:</span>
            <span style={styles.value}> UNKNOWN USER / UNVERIFIED</span>
          </div>
          <div style={styles.line}>
            <span style={styles.label}>NOTICE:</span>
            <span style={styles.value}> TRACE DETECTED — HUMAN OVERRIDE REQUIRED</span>
          </div>
          <div style={styles.promptLine}>
            <span style={styles.label}>&gt;</span>&nbsp;
            <span style={styles.value}>TRACE LOCKED ON DEVICE. Abort in time to access file.</span>
            <div style={styles.caret}></div>
          </div>
        </div>

        {gameStarted && (
          <div style={styles.game}>
            <div style={styles.gameTitle}>Trace override protocol</div>
            <div style={styles.gameInstruction}>
              Stop the trace inside the <strong>safe zone</strong> to abort tracking and unlock the file.
            </div>

            <div style={styles.traceBarWrap}>
              <div style={styles.traceLabelRow}>
                <span>Trace signal</span>
                <span>Safe zone</span>
              </div>
              <div style={styles.traceBar} ref={traceBarRef}>
                <div style={styles.traceSafeZone} ref={safeZoneRef}></div>
                <div style={styles.traceMarker} ref={traceMarkerRef}></div>
              </div>
              <div 
                style={styles.traceStatus} 
                dangerouslySetInnerHTML={{ __html: traceStatus }}
              />
            </div>

            <div style={styles.btnRow}>
              <button 
                style={styles.btn}
                onClick={checkHit}
                disabled={attempts >= maxAttempts && !isSuccess}
              >
                Abort Trace
              </button>
              {resultText && attempts < maxAttempts && !isSuccess && (
                <button 
                  style={{...styles.btn, ...styles.btnSecondary}}
                  onClick={startTrace}
                >
                  Recalibrate
                </button>
              )}
            </div>

            {resultText && (
              <div style={{
                ...styles.statusText,
                color: isSuccess ? '#10b981' : '#ef4444'
              }}>
                {resultText}
              </div>
            )}
          </div>
        )}
      </div>

      {showOffer && (
        <div style={{...styles.offer, ...styles.fadeIn}}>
          <small style={{color: '#10b981', fontWeight: 700}}>CLASSIFIED FILE UNLOCKED</small>
          <div style={styles.offerTitle}>Your local reward is ready.</div>
          <p>
            You just slipped past the trace. Next up: your
            <strong> exclusive Tappd offer</strong> for this location.
          </p>
          <p style={styles.tagline}>Routing you to the secure node…</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '"Courier New", monospace',
  },
  terminal: {
    background: '#1e293b',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    border: '1px solid #334155',
    transition: 'opacity 0.35s ease',
  },
  fadeOut: {
    opacity: 0,
  },
  fadeIn: {
    animation: 'fadeIn 0.5s ease forwards',
  },
  terminalHeader: {
    background: '#334155',
    padding: '12px 16px',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dots: {
    display: 'flex',
    gap: '6px',
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#64748b',
  },
  headerLabel: {
    color: '#e2e8f0',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '1px',
  },
  terminalBody: {
    padding: '24px',
    color: '#10b981',
    fontSize: '14px',
    lineHeight: '1.8',
  },
  line: {
    marginBottom: '8px',
  },
  label: {
    color: '#fbbf24',
    fontWeight: 700,
  },
  value: {
    color: '#10b981',
  },
  promptLine: {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
  },
  caret: {
    width: '8px',
    height: '16px',
    background: '#10b981',
    marginLeft: '4px',
    animation: 'blink 1s infinite',
  },
  game: {
    padding: '24px',
    borderTop: '1px solid #334155',
  },
  gameTitle: {
    color: '#fbbf24',
    fontSize: '18px',
    fontWeight: 700,
    marginBottom: '12px',
    textTransform: 'uppercase',
  },
  gameInstruction: {
    color: '#cbd5e1',
    fontSize: '14px',
    marginBottom: '24px',
  },
  traceBarWrap: {
    marginBottom: '24px',
  },
  traceLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#94a3b8',
    marginBottom: '8px',
  },
  traceBar: {
    position: 'relative',
    height: '40px',
    background: '#0f172a',
    borderRadius: '8px',
    border: '2px solid #334155',
    marginBottom: '12px',
  },
  traceSafeZone: {
    position: 'absolute',
    left: '40%',
    width: '20%',
    height: '100%',
    background: 'rgba(16, 185, 129, 0.2)',
    border: '2px solid #10b981',
    borderRadius: '6px',
  },
  traceMarker: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '28px',
    background: '#ef4444',
    boxShadow: '0 0 10px #ef4444',
    borderRadius: '2px',
    transition: 'left 0.05s linear',
  },
  traceStatus: {
    fontSize: '13px',
    color: '#cbd5e1',
  },
  btnRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  btn: {
    flex: 1,
    padding: '14px 24px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.2s',
  },
  btnSecondary: {
    background: '#64748b',
  },
  statusText: {
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center',
  },
  offer: {
    maxWidth: '600px',
    background: 'white',
    padding: '40px',
    borderRadius: '12px',
    textAlign: 'center',
    color: '#1e293b',
  },
  offerTitle: {
    fontSize: '28px',
    fontWeight: 700,
    margin: '16px 0',
    fontFamily: 'Fraunces, serif',
  },
  tagline: {
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: '16px',
  },
};

export default StopTheTrace;
